import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

// Enums matching backend
export type ProductType = "GOODS" | "PREPARED";
export type UnitType =
  | "PCS"
  | "KG"
  | "GM"
  | "MG"
  | "QUINTAL"
  | "LTR"
  | "ML"
  | "PACK"
  | "BOX"
  | "CARTON"
  | "CASE"
  | "DOZEN"
  | "BAG"
  | "TIN"
  | "CAN"
  | "BUNDLE"
  | "ROLL"
  | "SET"
  | "MTR"
  | "STRIP"
  | "TABLET"
  | "CAPSULE"
  | "BOTTLE"
  | "VIAL"
  | "AMPOULE"
  | "TUBE"
  | "SACHET"
  | "PLATE"
  | "PORTION"
  | "GLASS"
  | "CUP"
  | "BOWL"
  | "SERVING"
  | string;

export interface UnitOption {
  value: string;
  label: string;
  group: "General" | "Weight" | "Volume" | "Pharmacy" | "Restaurant";
}

export const UNIT_OPTIONS: UnitOption[] = [
  // General / Count
  { value: "PCS", label: "Pieces (PCS)", group: "General" },
  { value: "PACK", label: "Pack (PACK)", group: "General" },
  { value: "BOX", label: "Box (BOX)", group: "General" },
  { value: "CARTON", label: "Carton (CARTON)", group: "General" },
  { value: "CASE", label: "Case (CASE)", group: "General" },
  { value: "DOZEN", label: "Dozen (DOZEN)", group: "General" },
  { value: "BAG", label: "Bag (BAG)", group: "General" },
  { value: "TIN", label: "Tin (TIN)", group: "General" },
  { value: "CAN", label: "Can (CAN)", group: "General" },
  { value: "BUNDLE", label: "Bundle (BUNDLE)", group: "General" },
  { value: "ROLL", label: "Roll (ROLL)", group: "General" },
  { value: "SET", label: "Set (SET)", group: "General" },
  { value: "MTR", label: "Meter (MTR)", group: "General" },

  // Weight (Grocery & Kitchen)
  { value: "KG", label: "Kilogram (KG)", group: "Weight" },
  { value: "GM", label: "Gram (GM)", group: "Weight" },
  { value: "MG", label: "Milligram (MG)", group: "Weight" },
  { value: "QUINTAL", label: "Quintal (QUINTAL)", group: "Weight" },

  // Volume (Grocery & Beverage)
  { value: "LTR", label: "Liter (LTR)", group: "Volume" },
  { value: "ML", label: "Milliliter (ML)", group: "Volume" },

  // Medical / Pharmacy
  { value: "STRIP", label: "Strip (STRIP)", group: "Pharmacy" },
  { value: "TABLET", label: "Tablet (TABLET)", group: "Pharmacy" },
  { value: "CAPSULE", label: "Capsule (CAPSULE)", group: "Pharmacy" },
  { value: "BOTTLE", label: "Bottle (BOTTLE)", group: "Pharmacy" },
  { value: "VIAL", label: "Vial (VIAL)", group: "Pharmacy" },
  { value: "AMPOULE", label: "Ampoule (AMPOULE)", group: "Pharmacy" },
  { value: "TUBE", label: "Tube (TUBE)", group: "Pharmacy" },
  { value: "SACHET", label: "Sachet (SACHET)", group: "Pharmacy" },

  // Restaurant / Food Service
  { value: "PLATE", label: "Plate (PLATE)", group: "Restaurant" },
  { value: "PORTION", label: "Portion (PORTION)", group: "Restaurant" },
  { value: "GLASS", label: "Glass (GLASS)", group: "Restaurant" },
  { value: "CUP", label: "Cup (CUP)", group: "Restaurant" },
  { value: "BOWL", label: "Bowl (BOWL)", group: "Restaurant" },
  { value: "SERVING", label: "Serving (SERVING)", group: "Restaurant" }
];

export const ALLOWED_UNITS = UNIT_OPTIONS.map((u) => u.value);

export type AvailabilityType = "DINE_IN" | "TAKEAWAY" | "DELIVERY" | "ALL";
export type StatusType = "ACTIVE" | "INACTIVE";
export type DiscountType = "PERCENT" | "FLAT";

export interface Product {
  id: string;
  name: string;
  shortDescription: string | null;
  fullDescription: string | null;
  description?: string | null; // Added for compatibility
  productType: ProductType;
  vegFlag: boolean;
  sku: string;
  itemCode: string | null;
  hsnCode: string | null;
  barcode: string | null;
  categoryId: string;
  subCategoryId: string | null;
  unit: UnitType;
  basePrice: number;
  purchasePrice: number;
  sellingPrice: number;
  discountType: DiscountType | null;
  discountValue: number | null;
  gstPercentage: number;
  secondaryTaxRate?: number;
  secondaryTaxType?: string | null;
  taxCalculationType?: "ADDITIVE" | "COMPOUND";
  preparationTime: number | null;
  isPurchased: boolean;
  isPrepared: boolean;
  availability: AvailabilityType;
  isTaxInclusive: boolean;
  status: StatusType;
  hideFromBilling?: boolean;
  displayOrder: number;
  images: string[] | null;
  workCategories?: string[];
  packSize?: number;
  stockUnit?: UnitType | null;
  purchaseUnit?: UnitType | null;
  isActive: boolean;
  minimumStock?: number; // Added for inventory tracking
  maximumStock?: number; // Added for inventory tracking
  isTrackable?: boolean; // Added for inventory tracking
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt: string | null;
  category?: {
    id: string;
    name: string;
  };
  subCategory?: {
    id: string;
    name: string;
  };
}

export interface CreateProductData {
  categoryId: string;
  subCategoryId?: string;
  name: string;
  description?: string; // Simple description for create
  shortDescription?: string;
  fullDescription?: string;
  productType?: ProductType; // Made optional for create
  sku: string;
  itemCode?: string;
  hsnCode?: string;
  barcode?: string;
  unit: UnitType;
  basePrice?: number;
  purchasePrice?: number;
  sellingPrice?: number;
  unitPrice?: number; // Added for create form
  costPrice?: number; // Added for create form
  taxRate?: number; // Added for create form
  discountType?: DiscountType;
  discountValue?: number;
  gstPercentage?: number;
  secondaryTaxRate?: number;
  secondaryTaxType?: string | null;
  taxCalculationType?: "ADDITIVE" | "COMPOUND";
  preparationTime?: number;
  availability?: AvailabilityType;
  isTaxInclusive?: boolean;
  hideFromBilling?: boolean;
  displayOrder?: number;
  images?: string[];
  workCategories?: string[];
  packSize?: number;
  stockUnit?: UnitType | null;
  purchaseUnit?: UnitType | null;
}

export interface UpdateProductData {
  salesPointId?: string;
  categoryId?: string;
  subCategoryId?: string | null;
  name?: string;
  shortDescription?: string;
  fullDescription?: string;
  productType?: ProductType;
  sku?: string;
  itemCode?: string;
  hsnCode?: string;
  barcode?: string;
  unit?: UnitType;
  basePrice?: number;
  purchasePrice?: number;
  sellingPrice?: number;
  discountType?: DiscountType | null;
  discountValue?: number | null;
  gstPercentage?: number;
  secondaryTaxRate?: number;
  secondaryTaxType?: string | null;
  taxCalculationType?: "ADDITIVE" | "COMPOUND";
  preparationTime?: number | null;
  availability?: AvailabilityType;
  isTaxInclusive?: boolean;
  status?: StatusType;
  hideFromBilling?: boolean;
  displayOrder?: number;
  images?: string[] | null;
  isActive?: boolean;
  workCategories?: string[];
  packSize?: number;
  stockUnit?: UnitType | null;
  purchaseUnit?: UnitType | null;
}

const productAPI = {
  // Get all products with pagination
  getProducts: async (params?: {
    pageSize?: number;
    pageNo?: number;
    search?: string;
    categoryId?: string;
    productType?: ProductType;
    status?: StatusType;
    isActive?: boolean;
    availability?: AvailabilityType;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
    workCategory?: string;
  }): Promise<{ data: Product[]; total: number }> => {
    const response = await api.get("/products", { params });
    const { data, total } = parsePaginatedList<Product>(response.data.data);
    return { data, total };
  },

  // Get all products without pagination (for dropdowns)
  getAllProducts: async (): Promise<Product[]> => {
    const response = await api.get("/products/all");
    return response.data.data || [];
  },

  // Get product by ID
  getProductById: async (id: string): Promise<Product> => {
    const response = await api.get(`/products/${id}`);
    return response.data.data;
  },

  // Get product by SKU
  getProductBySku: async (sku: string): Promise<Product> => {
    const response = await api.get(`/products/sku/${sku}`);
    return response.data.data;
  },

  // Get product by barcode
  getProductByBarcode: async (barcode: string): Promise<Product> => {
    const response = await api.get(`/products/barcode/${barcode}`);
    return response.data.data;
  },

  // Create new product
  createProduct: async (data: CreateProductData): Promise<Product> => {
    const response = await api.post("/products", data);
    return response.data.data;
  },

  /** Super Admin: Excel/CSV → product_master (skips duplicate names) */
  importMasterExcel: async (
    file: File
  ): Promise<{
    total: number;
    created: number;
    skipped: number;
    failed: number;
    results: Array<{
      row: number;
      name: string;
      status: "created" | "skipped" | "failed";
      reason?: string;
      productId?: string;
    }>;
  }> => {
    const form = new FormData();
    form.append("file", file);
    const response = await api.post("/products/import-excel", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data;
  },

  // Update product
  updateProduct: async (
    id: string,
    data: UpdateProductData
  ): Promise<Product> => {
    const response = await api.put(`/products/${id}`, data);
    return response.data.data;
  },

  // Delete product
  deleteProduct: async (id: string): Promise<void> => {
    await api.delete(`/products/${id}`);
  },

  // Get product inventory
  getProductInventory: async (id: string): Promise<any> => {
    const response = await api.get(`/products/${id}/inventory`);
    return response.data.data;
  },

  // Get low stock products
  getLowStockProducts: async (): Promise<Product[]> => {
    const response = await api.get("/products/low-stock");
    return response.data.data;
  },

  // Get products by category
  getProductsByCategory: async (categoryId: string): Promise<Product[]> => {
    const response = await api.get(`/products/category/${categoryId}`);
    return response.data.data;
  },

  // Get products by salesPoint (full list for POS / billing — no page params)
  getProductsBySalesPoint: async (salesPointId: string): Promise<Product[]> => {
    const response = await api.get(`/product-sales-points/salesPoint/${salesPointId}`);
    const payload = response.data.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload?.items && Array.isArray(payload.items)) {
      return payload.items;
    }
    return [];
  },

  // Toggle product status
  toggleProductStatus: async (id: string): Promise<Product> => {
    const response = await api.patch(`/products/${id}/toggle-status`);
    return response.data.data;
  },

  // Get inventory by product
  getInventoryByProduct: async (productId: string): Promise<any> => {
    const response = await api.get(`/inventory/product/${productId}`);
    return response.data.data;
  },

  // Get inventory by product and branch
  getInventoryByProductAndBranch: async (
    productId: string,
    branchId: string
  ): Promise<any> => {
    const response = await api.get("/inventory/product-branch", {
      params: { productId, branchId },
    });
    return response.data.data;
  },

  // Update inventory
  updateInventory: async (id: string, data: any): Promise<any> => {
    const response = await api.put(`/inventory/${id}`, data);
    return response.data.data;
  },

  // Adjust inventory (add/remove stock)
  adjustInventory: async (id: string, data: any): Promise<any> => {
    const response = await api.post(`/inventory/${id}/adjust`, data);
    return response.data.data;
  },

  // Get low stock items
  getLowStockItems: async (): Promise<any> => {
    const response = await api.get("/inventory/low-stock");
    return response.data.data;
  },

  // Get inventory report
  getInventoryReport: async (): Promise<any> => {
    const response = await api.get("/inventory/report");
    return response.data.data;
  },

  // Delete inventory
  deleteInventory: async (id: string): Promise<void> => {
    await api.delete(`/inventory/${id}`);
  },
};

export { productAPI };
